import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Bucket, BucketCounts, WindowKey } from '../../services/executionStatsService';
import {
    ATTENTION_BUCKETS, ATTENTION_SHORT, BUCKET_LABELS, BUCKET_ORDER, BUCKET_SHORT, FAILURE_RATE_DEFINITION,
    FLOW_ATTRIBUTION, NOT_TRACKED, TOTAL_DEFINITION, attentionInk, bucketDefinition, count, countInk, failureRate,
    percent, relativeTime, trackedFromNote
} from './executionStatsVocabulary';
import { BADGE_WINDOWS, windowLabel } from './executionStatsWindows';
import { SummaryState, useExecutionSummary, useWorkflowRunStats } from './useExecutionSummary';
import { dsVar, ink } from '../../theme/tokens';

export type BadgeVariant = 'compact' | 'full';

interface ExecutionStatsBadgeProps {
    state: SummaryState;
    attribution?: 'campaign' | 'flow' | null;
    variant?: BadgeVariant;
    defaultWindow?: WindowKey;
    /** Opens the runs behind a count. Counts are only clickable when this is set. */
    onSelectBucket?: (bucket: Bucket | null, window: WindowKey) => void;
    /** Full variant only: a line under the verdict, such as whether the workflow has recovered. */
    insight?: React.ReactNode;
    /** Called when the reader switches window, so a caller can keep its own copy. */
    onWindowChange?: (window: WindowKey) => void;
}

const muted = { fontSize: '0.75rem', color: dsVar('fg-muted') };

/** Every bucket's meaning, for the compact badge's tooltip. */
const definitionsTooltip = (staleAfterHours: number, attribution?: 'campaign' | 'flow' | null) => (
    <Box sx={{ p: 0.5 }}>
        {BUCKET_ORDER.map((bucket) => (
            <Typography key={bucket} sx={{ fontSize: '0.6875rem', mb: 0.5 }}>
                <strong>{BUCKET_LABELS[bucket]}</strong> — {bucketDefinition(bucket, staleAfterHours)}
            </Typography>
        ))}
        {attribution === 'flow' && (
            <Typography sx={{ fontSize: '0.6875rem', mt: 0.5, fontStyle: 'italic' }}>{FLOW_ATTRIBUTION}</Typography>
        )}
    </Box>
);

/** "41 runs · 38 ✓ · 2 ✗ · 1 stalled · 7d" — one line, for a card or a header. */
const CompactBadge: React.FC<{
    counts: BucketCounts;
    window: WindowKey;
    staleAfterHours: number;
    attribution?: 'campaign' | 'flow' | null;
}> = ({ counts, window, staleAfterHours, attribution }) => {
    // Only buckets with something in them earn a place; a quiet workflow reads
    // as its total alone rather than a row of zeros.
    const parts = BUCKET_ORDER.filter((bucket) => {
        const value = counts[bucket];
        return bucket === 'success' || bucket === 'failed' ? true : Boolean(value);
    });
    return (
        <Tooltip title={definitionsTooltip(staleAfterHours, attribution)} arrow placement="top">
            <Box
                component="span"
                aria-label={`Runs in the ${windowLabel(window)}`}
                sx={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0 6px', cursor: 'help' }}
            >
                <Typography component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), fontWeight: 600 }}>
                    {count(counts.total)} {counts.total === 1 ? 'run' : 'runs'}
                </Typography>
                {counts.total > 0 && parts.map((bucket) => {
                    const value = counts[bucket];
                    return (
                        <Typography
                            key={bucket}
                            component="span"
                            title={BUCKET_LABELS[bucket]}
                            sx={{ fontSize: '0.75rem', color: ink(countInk(bucket, value)) }}
                        >
                            · {value === null ? '—' : count(value)} {BUCKET_SHORT[bucket]}
                        </Typography>
                    );
                })}
                <Typography component="span" sx={muted}>· {window}{attribution === 'flow' ? ' · all runs' : ''}</Typography>
            </Box>
        </Tooltip>
    );
};

/** One labelled figure in the full badge. */
const StatTile: React.FC<{
    label: string;
    value: number | null;
    definition: string;
    ink?: string;
    detail?: string;
    onClick?: () => void;
}> = ({ label, value, definition, ink: tone, detail, onClick }) => {
    const interactive = Boolean(onClick) && value !== null && value > 0;
    return (
        <Box
            component={interactive ? 'button' : 'div'}
            onClick={interactive ? onClick : undefined}
            sx={{
                textAlign: 'left',
                p: 1.25,
                borderRadius: '8px',
                border: `1px solid ${dsVar('line')}`,
                backgroundColor: dsVar('page', 0.4),
                cursor: interactive ? 'pointer' : 'default',
                font: 'inherit',
                '&:hover': interactive ? { borderColor: 'rgba(255, 222, 92, 0.5)' } : undefined
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-2'), fontWeight: 500 }}>{label}</Typography>
                <Tooltip title={definition} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: dsVar('fg-muted'), cursor: 'help' }} />
                </Tooltip>
            </Box>
            {value === null ? (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.5, fontStyle: 'italic' }}>
                    {NOT_TRACKED}
                </Typography>
            ) : (
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: value > 0 ? (tone ? ink(tone) : dsVar('fg')) : dsVar('fg-muted'), lineHeight: 1.3 }}>
                    {count(value)}
                </Typography>
            )}
            {detail && <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted') }}>{detail}</Typography>}
        </Box>
    );
};

/**
 * The one-line answer to "is anything wrong", above the figures.
 *
 * Problems are named with their counts; an all-clear says so in words rather
 * than leaving the reader to add up six tiles.
 */
const Verdict: React.FC<{ counts: BucketCounts; recent?: BucketCounts; window: WindowKey }> = ({
    counts, recent, window
}) => {
    if (counts.total === 0) {
        return <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>No runs in the {windowLabel(window)}.</Typography>;
    }
    const flagged = ATTENTION_BUCKETS.filter((bucket) => (counts[bucket] ?? 0) > 0);
    if (!flagged.length) {
        const running = counts.running ?? 0;
        const text = counts.success === counts.total
            ? `All ${count(counts.total)} ${counts.total === 1 ? 'run' : 'runs'} succeeded in the ${windowLabel(window)}.`
            : `No problems in the ${windowLabel(window)} · ${count(counts.success)} of ${count(counts.total)} succeeded` +
              (running ? `, ${count(running)} still running.` : '.');
        return <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft'), fontWeight: 600 }}>{text}</Typography>;
    }
    // Of the flagged runs, how many started in the last day: failing now,
    // rather than failed at some point in the window.
    const recentFlagged = recent ? flagged.reduce((sum, bucket) => sum + (recent[bucket] ?? 0), 0) : 0;
    return (
        <Typography sx={{ fontSize: '0.875rem', color: ink(attentionInk(flagged[0])), fontWeight: 700 }}>
            {flagged.map((bucket) => `${count(counts[bucket] as number)} ${ATTENTION_SHORT[bucket]}`).join(' · ')}
            {' '}in the {windowLabel(window)}
            {recentFlagged > 0 && (
                <Typography component="span" sx={{ fontSize: 'inherit', color: dsVar('fg-soft'), fontWeight: 600 }}>
                    {' '}· {count(recentFlagged)} in the last 24 hours
                </Typography>
            )}
        </Typography>
    );
};

/** A secondary figure: quiet, but still opens its runs. */
const MinorCount: React.FC<{ value: number; label: string; onClick?: () => void }> = ({ value, label, onClick }) => {
    const interactive = Boolean(onClick) && value > 0;
    return (
        <Box
            component={interactive ? 'button' : 'span'}
            onClick={interactive ? onClick : undefined}
            sx={{
                background: 'none', border: 'none', p: 0, font: 'inherit', fontSize: '0.75rem', color: dsVar('fg-2'),
                cursor: interactive ? 'pointer' : 'default',
                '&:hover': interactive ? { color: dsVar('accent-fg') } : undefined
            }}
        >
            <span>{count(value)}</span> {label}
        </Box>
    );
};

/**
 * The full figures, problems first, with a window toggle that never refetches.
 *
 * A verdict line, then a tile per bucket that needs a person — failed, stalled,
 * pending review — and only then the totals, as one quiet line. Every figure
 * still opens the runs behind it.
 */
const FullBadge: React.FC<{
    data: NonNullable<SummaryState['data']>;
    window: WindowKey;
    onWindow: (window: WindowKey) => void;
    attribution?: 'campaign' | 'flow' | null;
    onSelectBucket?: (bucket: Bucket | null, window: WindowKey) => void;
    insight?: React.ReactNode;
}> = ({ data, window, onWindow, attribution, onSelectBucket, insight }) => {
    const counts = data.counts[window];
    const recent = window === '24h' ? undefined : data.counts['24h'];
    const select = (bucket: Bucket | null) => (onSelectBucket ? () => onSelectBucket(bucket, window) : undefined);
    const flagged = counts ? ATTENTION_BUCKETS.filter((bucket) => (counts[bucket] ?? 0) > 0) : [];
    const untracked = counts
        ? (['pendingReview', 'running'] as Bucket[]).filter((bucket) => counts[bucket] === null)
        : [];
    // A shorter window may already count reviews; say from when.
    const reviewsFrom = trackedFromNote(data.runtimeStatusSince);
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                    Runs in the {windowLabel(window)}
                </Typography>
                <Box role="group" aria-label="Window" sx={{ display: 'flex', gap: 0.5 }}>
                    {BADGE_WINDOWS.map((w) => (
                        <Box
                            key={w.key}
                            component="button"
                            aria-pressed={w.key === window}
                            onClick={() => onWindow(w.key)}
                            sx={{
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                px: 1,
                                py: 0.25,
                                borderRadius: '999px',
                                border: '1px solid',
                                borderColor: w.key === window ? dsVar('accent-fg') : dsVar('line'),
                                color: w.key === window ? dsVar('on-accent') : dsVar('fg-2'),
                                backgroundColor: w.key === window ? dsVar('accent') : 'transparent',
                                cursor: 'pointer'
                            }}
                        >
                            {w.label}
                        </Box>
                    ))}
                </Box>
            </Box>
            {counts ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Verdict counts={counts} recent={recent} window={window} />
                    {insight}
                    {flagged.length > 0 && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${flagged.length}, minmax(0, 1fr))`, gap: 1 }}>
                            {flagged.map((bucket) => (
                                <StatTile
                                    key={bucket}
                                    label={BUCKET_LABELS[bucket]}
                                    value={counts[bucket]}
                                    ink={attentionInk(bucket)}
                                    definition={bucket === 'failed'
                                        ? `${bucketDefinition(bucket, data.staleAfterHours)} ${FAILURE_RATE_DEFINITION}`
                                        : bucketDefinition(bucket, data.staleAfterHours)}
                                    detail={bucket === 'failed' && failureRate(counts) !== null
                                        ? `${percent(failureRate(counts))} of finished`
                                        : undefined}
                                    onClick={select(bucket)}
                                />
                            ))}
                        </Box>
                    )}
                    {counts.total > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 6px' }}>
                            <Tooltip title={TOTAL_DEFINITION} arrow placement="top" describeChild>
                                <span><MinorCount value={counts.total} label="total" onClick={select(null)} /></span>
                            </Tooltip>
                            <Typography component="span" sx={muted}>·</Typography>
                            <MinorCount value={counts.success} label="succeeded" onClick={select('success')} />
                            {Boolean(counts.running) && (
                                <>
                                    <Typography component="span" sx={muted}>·</Typography>
                                    <MinorCount value={counts.running as number} label="running" onClick={select('running')} />
                                </>
                            )}
                        </Box>
                    )}
                    {/* An untracked bucket is said to be one, never shown as 0. */}
                    {untracked.map((bucket) => (
                        <Typography key={bucket} sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                            {BUCKET_LABELS[bucket]}: <Box component="span" sx={{ fontStyle: 'italic' }}>{NOT_TRACKED}</Box>
                            {bucket === 'pendingReview' && reviewsFrom && ` · ${reviewsFrom.toLowerCase()}`}
                        </Typography>
                    ))}
                </Box>
            ) : (
                <Typography sx={muted}>No figures for this window.</Typography>
            )}
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {attribution === 'flow' && (
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-2'), fontStyle: 'italic' }}>
                        {FLOW_ATTRIBUTION}
                    </Typography>
                )}
                {attribution === 'campaign' && data.lastRunAt !== undefined && (
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                        Last run {relativeTime(data.lastRunAt ?? null)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

/**
 * Run counts for one flow, node or campaign.
 *
 * Presentational: the numbers come in through `state`, from
 * useExecutionSummary or useWorkflowRunStats, so a page of badges shares one
 * request. A count the platform can't measure reads "Not yet tracked", never 0.
 */
const ExecutionStatsBadge: React.FC<ExecutionStatsBadgeProps> = ({
    state,
    attribution,
    variant = 'compact',
    defaultWindow = '7d',
    onSelectBucket,
    insight,
    onWindowChange
}) => {
    const [window, setWindowState] = useState<WindowKey>(defaultWindow);
    const setWindow = (next: WindowKey) => {
        setWindowState(next);
        onWindowChange?.(next);
    };

    if (state.loading && !state.data) {
        return <Typography component="span" sx={muted}>Loading runs…</Typography>;
    }
    if (state.error || !state.data) {
        return (
            <Typography component="span" sx={muted} title={state.error ?? undefined}>
                Run statistics unavailable
            </Typography>
        );
    }

    if (variant === 'full') {
        return (
            <FullBadge
                data={state.data}
                window={window}
                onWindow={setWindow}
                attribution={attribution}
                onSelectBucket={onSelectBucket}
                insight={insight}
            />
        );
    }

    const counts = state.data.counts[window];
    if (!counts) return null;
    return (
        <CompactBadge
            counts={counts}
            window={window}
            staleAfterHours={state.data.staleAfterHours}
            attribution={attribution}
        />
    );
};

/** A workflow card's badge: the campaign's own runs, else the flow's. */
export const WorkflowRunBadge: React.FC<{
    campaignId?: string | null;
    flowCode?: string | null;
    variant?: BadgeVariant;
    defaultWindow?: WindowKey;
    onSelectBucket?: (bucket: Bucket | null, window: WindowKey, attribution: 'campaign' | 'flow') => void;
}> = ({ campaignId, flowCode, variant, defaultWindow, onSelectBucket }) => {
    const stats = useWorkflowRunStats({ campaignId, flowCode });
    return (
        <ExecutionStatsBadge
            state={stats}
            attribution={stats.attribution}
            variant={variant}
            defaultWindow={defaultWindow}
            onSelectBucket={
                onSelectBucket && stats.attribution
                    ? (bucket, window) => onSelectBucket(bucket, window, stats.attribution!)
                    : undefined
            }
        />
    );
};

/** A flow template's badge, across every version of it. */
export const FlowRunBadge: React.FC<{
    flowCode: string;
    variant?: BadgeVariant;
    defaultWindow?: WindowKey;
    onSelectBucket?: (bucket: Bucket | null, window: WindowKey) => void;
}> = ({ flowCode, variant, defaultWindow, onSelectBucket }) => {
    const state = useExecutionSummary({ scope: 'flow', keyType: 'code', key: flowCode, groupVersions: true });
    return (
        <ExecutionStatsBadge
            state={state}
            variant={variant}
            defaultWindow={defaultWindow}
            onSelectBucket={onSelectBucket}
        />
    );
};

/**
 * An agent node's badge, across every version of its template.
 *
 * Callers gate this on environment.executionStatsNodeScope. A node is never
 * seen running — its row is written when it returns — so a node badge shows
 * outcomes and pending reviews only.
 */
export const AgentRunBadge: React.FC<{ activityTemplateCode: string; defaultWindow?: WindowKey }> = ({
    activityTemplateCode,
    defaultWindow
}) => {
    const state = useExecutionSummary({
        scope: 'node', keyType: 'code', key: activityTemplateCode, groupVersions: true
    });
    return <ExecutionStatsBadge state={state} variant="compact" defaultWindow={defaultWindow} />;
};

export default ExecutionStatsBadge;
